import { useMemo, useState } from "react";
import { Input, Spinner, Tab, TabList, Text, Title3 } from "@fluentui/react-components";
import { Search20Regular } from "@fluentui/react-icons";
import { useTickets } from "../../api/hooks";
import { TicketTable, isClosed } from "../../components/TicketTable";
import type { Priority } from "../../types/models";

type TabKey = "open" | "closed";

const PRIORITY_COLOR: Record<string, string> = {
  Critical: "#D13438",
  High: "#F7630C",
  Medium: "#0078D4",
  Low: "#8A8886",
};

export function AgentQueuePage() {
  const [tab, setTab] = useState<TabKey>("open");
  const [search, setSearch] = useState("");
  const [priority, setPriority] = useState<Priority | "all">("all");

  const tickets = useTickets({ sort: "number_asc", pageSize: 500 });
  const all = tickets.data?.items ?? [];

  const openItems = all.filter((t) => !isClosed(t.status));
  const closedItems = all.filter((t) => isClosed(t.status));

  const filtered = useMemo(() => {
    const base = tab === "open" ? openItems : closedItems;
    const q = search.trim().toLowerCase();
    return base.filter((t) => {
      if (priority !== "all" && t.priority !== priority) return false;
      if (!q) return true;
      return (
        t.title.toLowerCase().includes(q) ||
        `it-${t.ticketNumber}`.includes(q) ||
        (t.requester?.displayName ?? "").toLowerCase().includes(q) ||
        (t.assignee?.displayName ?? "").toLowerCase().includes(q)
      );
    });
  }, [openItems, closedItems, tab, search, priority]);

  const unassigned = openItems.filter((t) => !t.assigneeId).length;
  const breached = openItems.filter(
    (t) => t.slaDueAt && new Date(t.slaDueAt) < new Date()
  ).length;
  const critical = openItems.filter((t) => t.priority === "Critical").length;

  return (
    <div>
      <Title3>Queue</Title3>

      {/* Quick stats row */}
      <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <QueueStat value={openItems.length} label="Open" color="#0078D4" />
        <QueueStat value={unassigned} label="Unassigned" color={unassigned > 0 ? "#F7630C" : "#8A8886"} />
        {breached > 0 && <QueueStat value={breached} label="SLA Breached" color="#D13438" />}
        {critical > 0 && <QueueStat value={critical} label="Critical" color="#D13438" />}
        <QueueStat value={closedItems.length} label="Closed" color="#8A8886" />
      </div>

      {/* Tab + search row */}
      <div style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        marginTop: 18,
        flexWrap: "wrap",
        justifyContent: "space-between",
      }}>
        <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(d.value as TabKey)}>
          <Tab value="open">Open ({openItems.length})</Tab>
          <Tab value="closed">Closed ({closedItems.length})</Tab>
        </TabList>
        <Input
          contentBefore={<Search20Regular />}
          placeholder="Search title, #, requester…"
          value={search}
          onChange={(_, d) => setSearch(d.value)}
          style={{ minWidth: 240 }}
        />
      </div>

      {/* Priority filter pills */}
      <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
        <Text size={200} style={{ color: "#8A8886", marginRight: 4 }}>Priority:</Text>
        {(["all", "Critical", "High", "Medium", "Low"] as Array<Priority | "all">).map((p) => {
          const active = priority === p;
          const color = p === "all" ? "#605E5C" : PRIORITY_COLOR[p];
          const base = tab === "open" ? openItems : closedItems;
          const cnt = p === "all" ? filtered.length : base.filter((t) => t.priority === p).length;
          return (
            <button
              key={p}
              onClick={() => setPriority(p)}
              style={{
                padding: "3px 11px",
                borderRadius: 999,
                border: `1.5px solid ${active ? color : "#EDEBE9"}`,
                background: active ? `${color}15` : "#F8F8F8",
                color: active ? color : "#605E5C",
                fontWeight: active ? 700 : 400,
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all 0.12s",
              }}
            >
              {p === "all" ? "All" : p}{" "}
              <span style={{ opacity: 0.7, fontWeight: 400 }}>({cnt})</span>
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 14 }}>
        {tickets.isLoading ? (
          <div style={{ padding: "40px 0", textAlign: "center" }}>
            <Spinner label="Loading queue…" />
          </div>
        ) : (
          <>
            <TicketTable tickets={filtered} showRequester editable />
            {tickets.data && tickets.data.total > all.length && (
              <p style={{ marginTop: 10, color: "#8A8886", fontSize: 12 }}>
                Showing {all.length} of {tickets.data.total} tickets.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function QueueStat({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 16px",
      borderRadius: 8,
      background: `${color}0D`,
      border: `1px solid ${color}30`,
      minWidth: 100,
    }}>
      <span style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{value}</span>
      <span style={{ fontSize: 12, color: "#605E5C", lineHeight: "14px" }}>{label}</span>
    </div>
  );
}
